// src/pages/Onboarding.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { createBusiness } from '../lib/airtable';
import Nav from '../components/Nav';

const STEPS = [
  { id: 'business', title: 'Your Business', subtitle: 'Tell us about your business' },
  { id: 'hours',    title: 'Business Hours', subtitle: 'When are you open?' },
  { id: 'knowledge', title: 'AI Receptionist Knowledge', subtitle: 'What should Sophie know about your business?' },
  { id: 'services', title: 'Services', subtitle: 'What do you offer?' },
  { id: 'sophie',   title: 'Your AI Agent', subtitle: 'Set up your receptionist' },
];

export default function Onboarding() {
  const { user, refreshBusiness } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fields match the real Airtable Business Settings schema exactly
  const [form, setForm] = useState({
    'Business Name': '',
    'Transfer Number': '',
    'AI Agent Name': 'Sophie',
    'Weekday Hours': '8:00 AM – 6:00 PM',
    'Weekend Hours': 'Closed',
    'AI Knowledge': '',
    'Service Zip Codes': '',
    'Services Offered': '',
    'Pricing Table': '',
    'Special Instructions': '',
    'Owner Email': user?.email || '',
  });

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function next() { setError(''); setStep(s => Math.min(s + 1, STEPS.length - 1)); }
  function back() { setStep(s => Math.max(s - 1, 0)); }

  async function submit() {
    setError('');
    setSaving(true);
    try {
      // Ensure the owner email is current
      const payload = { ...form, 'Owner Email': user?.email || form['Owner Email'] };
      console.log('📝 Submitting form:', payload);

      const result = await createBusiness(payload);
      console.log('✅ Business created:', result);

      if (!result || !result.id) {
        throw new Error('Airtable did not return a record. Check that all field names match your Airtable schema (especially "AI Knowledge" — must be added as Long text).');
      }

      console.log('🔄 Refreshing business...');
      // Small delay to let Airtable index the new record before we query by email
      await new Promise(r => setTimeout(r, 800));
      const refreshed = await refreshBusiness();
      console.log('✅ Business refreshed:', refreshed);

      if (!refreshed) {
        // Try one more time
        await new Promise(r => setTimeout(r, 1200));
        const retry = await refreshBusiness();
        if (!retry) {
          throw new Error('Your settings were saved to Airtable, but we could not find the record by your email. Please refresh the page — you should land on the dashboard.');
        }
      }

      console.log('🚀 Navigating to dashboard...');
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message || 'Something went wrong saving your settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="page">
      <Nav />
      <div style={{ flex: 1, padding: '40px 0' }}>
        <div className="container-sm">

          {/* Progress */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Step {step + 1} of {STEPS.length}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ring)' }}>{STEPS[step].title}</span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 999 }}>
              <div style={{ height: '100%', width: `${progress}%`, background: 'var(--ring)', borderRadius: 999, transition: 'width 0.3s ease' }} />
            </div>
          </div>

          <div className="card fade-up">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{STEPS[step].title}</h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 28 }}>{STEPS[step].subtitle}</p>

            {error && <div className="alert alert-error">{error}</div>}

            {/* Step 0: Business basics */}
            {step === 0 && <>
              <div className="form-group">
                <label className="form-label">Business name <span>*</span></label>
                <input className="form-input" placeholder="e.g. Avengers Cleaning Services" value={form['Business Name']} onChange={set('Business Name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Your phone number <span>* (Sophie transfers calls here)</span></label>
                <input className="form-input" type="tel" placeholder="e.g. (555) 123-4567" value={form['Transfer Number']} onChange={set('Transfer Number')} required />
                <span className="form-hint">When a caller needs to speak with a human, Sophie transfers them to this number.</span>
              </div>
              <div className="form-group">
                <label className="form-label">Service zip codes <span>(one per line)</span></label>
                <textarea className="form-textarea" placeholder={"77001\n77002\n77003"} value={form['Service Zip Codes']} onChange={set('Service Zip Codes')} style={{ minHeight: 80 }} />
                <span className="form-hint">The zip codes your business serves. Sophie will confirm if a customer is in your area.</span>
              </div>
            </>}

            {/* Step 1: Business Hours (weekday + weekend) */}
            {step === 1 && <>
              <div className="form-group">
                <label className="form-label">Weekday hours (Mon–Fri)</label>
                <input className="form-input" placeholder="e.g. 8:00 AM – 6:00 PM" value={form['Weekday Hours']} onChange={set('Weekday Hours')} />
              </div>
              <div className="form-group">
                <label className="form-label">Weekend hours (Sat & Sun)</label>
                <input className="form-input" placeholder="e.g. Closed or 9:00 AM – 3:00 PM" value={form['Weekend Hours']} onChange={set('Weekend Hours')} />
              </div>
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                💡 Sophie will answer calls 24/7 but will let customers know your actual business hours when scheduling. You can set day-by-day hours later in Settings.
              </div>
            </>}

            {/* Step 2: AI Receptionist Knowledge */}
            {step === 2 && <>
              <div className="form-group">
                <label className="form-label">AI Receptionist Knowledge <span>(optional)</span></label>
                <textarea
                  className="form-textarea"
                  placeholder={`Tell Sophie important information about your business:\n\nExample:\n- We specialize in eco-friendly cleaning using non-toxic products\n- We offer same-day appointments for emergencies\n- Our average service takes 2-3 hours\n- We're fully booked on Tuesdays and Wednesdays\n- Always ask about allergies or pet sensitivities`}
                  value={form['AI Knowledge']}
                  onChange={set('AI Knowledge')}
                  style={{ minHeight: 150 }}
                />
                <span className="form-hint">Share any important details, policies, or personality traits Sophie should know. You can edit this anytime from the AI Receptionist Knowledge page.</span>
              </div>
            </>}

            {/* Step 3: Services & pricing */}
            {step === 3 && <>
              <div className="form-group">
                <label className="form-label">Services offered <span>(one per line)</span></label>
                <textarea className="form-textarea" placeholder={"Standard Cleaning\nDeep Cleaning\nMove-Out Cleaning\nOffice Cleaning"} value={form['Services Offered']} onChange={set('Services Offered')} style={{ minHeight: 120 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Pricing <span>(optional — one per line)</span></label>
                <textarea className="form-textarea" placeholder={"Standard Cleaning: $120\nDeep Cleaning: $200\nMove-Out Cleaning: $250"} value={form['Pricing Table']} onChange={set('Pricing Table')} style={{ minHeight: 100 }} />
                <span className="form-hint">Sophie will share these prices when customers ask. Leave blank and Sophie will say pricing varies.</span>
              </div>
              <div className="form-group">
                <label className="form-label">Special instructions for Sophie <span>(optional)</span></label>
                <textarea className="form-textarea" placeholder="e.g. Always confirm the customer's zip code before booking. We are fully booked on Tuesdays." value={form['Special Instructions']} onChange={set('Special Instructions')} style={{ minHeight: 80 }} />
              </div>
            </>}

            {/* Step 4: Sophie setup */}
            {step === 4 && <>
              <div className="form-group">
                <label className="form-label">AI receptionist name</label>
                <input className="form-input" placeholder="Sophie" value={form['AI Agent Name']} onChange={set('AI Agent Name')} />
                <span className="form-hint">The name your AI receptionist will use when answering calls. Most clients keep it as Sophie.</span>
              </div>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>📋 Here's what Sophie will say:</div>
                <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, fontStyle: 'italic' }}>
                  "Thank you for calling <strong>{form['Business Name'] || 'your business'}</strong>, this is <strong>{form['AI Agent Name'] || 'Sophie'}</strong>, how can I help you today?"
                </div>
              </div>
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                🚀 After you finish, your settings save to our system. Your RingXai team will activate your AI agent within 24 hours and send you a confirmation.
              </div>
            </>}

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, gap: 12 }}>
              {step > 0
                ? <button className="btn btn-ghost" onClick={back}>← Back</button>
                : <div />
              }
              {step < STEPS.length - 1
                ? <button className="btn btn-primary" onClick={next} disabled={step === 0 && !form['Business Name']}>
                    Continue →
                  </button>
                : <button className="btn btn-primary" onClick={submit} disabled={saving}>
                    {saving ? <><span className="spinner" /> Saving...</> : 'Finish setup →'}
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
